import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as employeesApi from '../../api/employees';
import { formatDate } from '../../utils/format';

const ROLE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'admin', label: 'Admin' },
];
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'locked', label: 'Locked' },
];

const emptyForm = { name: '', email: '', password: '', role: 'employee', phone: '', department: '', designation: '', joiningDate: '' };

export default function Employees() {
  const { actor } = useAuth();
  const isSuperAdmin = actor.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    employeesApi
      .listEmployees({ page, search, status: statusFilter || undefined })
      .then((res) => {
        setRows(res.data);
        setPagination(res.pagination);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load employees.'))
      .finally(() => setLoading(false));
  }, [page, search, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (row) => {
    setForm({ name: row.name, phone: row.phone || '', department: row.department || '', designation: row.designation || '', joiningDate: row.joiningDate || '' });
    setEditingId(row.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await employeesApi.updateEmployee(editingId, form);
        setSuccess('Employee profile updated.');
      } else {
        await employeesApi.createEmployee(form);
        setSuccess('Employee account created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save employee.');
    }
  };

  const handleStatusChange = async (row, status) => {
    try {
      await employeesApi.setEmployeeStatus(row.id, status);
      setSuccess(`${row.name}'s status set to ${status}.`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status.');
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    try {
      await employeesApi.setEmployeePassword(passwordTarget.id, newPassword);
      setSuccess(`Password updated for ${passwordTarget.name}.`);
      setPasswordTarget(null);
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update password.');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove ${row.name}'s account? This cannot be undone.`)) return;
    try {
      await employeesApi.deleteEmployee(row.id);
      setSuccess('Employee removed.');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete employee.');
    }
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'role', label: 'Role' },
    { key: 'department', label: 'Department', render: (r) => r.department || '-' },
    { key: 'designation', label: 'Designation', render: (r) => r.designation || '-' },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'joiningDate', label: 'Joined', render: (r) => formatDate(r.joiningDate) },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="btn-group">
          <button type="button" className="btn-link" onClick={() => openEdit(r)}>Edit</button>
          {isSuperAdmin && r.role !== 'super_admin' && (
            <>
              <button type="button" className="btn-link" onClick={() => setPasswordTarget(r)}>Reset Password</button>
              {r.status === 'active' ? (
                <button type="button" className="btn-link" onClick={() => handleStatusChange(r, 'suspended')}>Suspend</button>
              ) : (
                <button type="button" className="btn-link" onClick={() => handleStatusChange(r, 'active')}>Activate</button>
              )}
              <button type="button" className="btn-link" onClick={() => handleDelete(r)}>Delete</button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Employees">
      <div className="page-header">
        <div>
          <h1 className="heading">Employees</h1>
          <p className="page-subtext">There is no limit on the number of staff accounts the firm can create.</p>
        </div>
        {isSuperAdmin && (
          <button type="button" className="btn" onClick={openCreate}>Add Employee</button>
        )}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      <div className="filter-bar">
        <input
          type="text"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <Loader text="Loading employees..." />
      ) : (
        <>
          <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No employees found." />
          <Pagination pagination={pagination} onPageChange={setPage} />
        </>
      )}

      {showForm && (
        <Modal
          title={editingId ? 'Edit Employee' : 'Add Employee'}
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="employee-form" className="btn">Save</button>
            </>
          }
        >
          <form id="employee-form" onSubmit={handleSave}>
            <div className="form-grid single-column">
              <FormField label="Full Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              {!editingId && (
                <>
                  <FormField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  <FormField label="Initial Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required hint="The employee cannot change this themselves - only the Super Admin can reset it later." />
                  <FormField label="Role" type="select" options={ROLE_OPTIONS} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
                </>
              )}
              <FormField label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <FormField label="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
              <FormField label="Designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
              <FormField label="Joining Date" type="date" value={form.joiningDate} onChange={(e) => setForm({ ...form, joiningDate: e.target.value })} />
            </div>
          </form>
        </Modal>
      )}

      {passwordTarget && (
        <Modal
          title={`Reset Password - ${passwordTarget.name}`}
          onClose={() => setPasswordTarget(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setPasswordTarget(null)}>Cancel</button>
              <button type="submit" form="password-form" className="btn">Update Password</button>
            </>
          }
        >
          <form id="password-form" onSubmit={handleSetPassword}>
            <FormField label="New Password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required hint="Minimum 6 characters. Share this with the employee securely." />
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}
